import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useMutation } from '@tanstack/react-query'
import { DadosCadastrais, Endereco } from '../types/devedor-interface'
import { apiModulo } from '../services/api'
import { useDividas } from "./DividasContext";
import { AtualizaEmailProps } from "../types/email-atualiza-interface";
import { AtualizaTelefoneProps, MelhorHorarioProps } from "../types/telefone-atualiza-interface";
import { useModal } from "../hooks/ModalContext";
import { DevedorData } from "../types/devedor-data-interface";

interface CadastroContextProviderProps {
  children: ReactNode;
}

interface AtualizaCadastroProps {
  cpf_cnpj: string;
  url: string;
  codcredor: string;
  nomeclien: string;
  datnasc?: string;
  dadoscadastrais?: DadosCadastrais;
  enderecos?: {
    endereco: Endereco
  };
  telefones: {
    telefonecliente: AtualizaTelefoneProps[];
  }
  emails: {
    emailcliente: AtualizaEmailProps[];
  }
  usuario?: string
  app?: string
}

interface AtualizaDevedorProps {
  cpf_cnpj: string,
  url: string,
  codcredor: string,
  nomeclien: string;
  datnasc: string;
  email?: AtualizaEmailProps,
  telefone?: AtualizaTelefoneProps
  dadosCadastrais?: DadosCadastrais,
  endereco?: Endereco,
  usuario?: string
  app?: string
}

interface CadastroContextProps {
  atualizaDevedorCadastro({ dadosCadastrais, endereco, codcredor, cpf_cnpj, url }: AtualizaDevedorProps): Promise<number>;
  getEmail(email: AtualizaEmailProps, emailInfo?: string): void;
  getTelefones(telefone: AtualizaTelefoneProps, infoTel?: string): void;
  getMelhoresHorarios(melhorhorario: MelhorHorarioProps[], telefone: string): void
  deleteMelhorHoario(dia: string, telefone: string): void
  clearCadastro(): void
  getClientData(data: React.SetStateAction<DevedorData>): void
  getUltimoCPF(cpf: string): void
  setInadimpelcia: React.Dispatch<React.SetStateAction<boolean>>
  setEndereco: React.Dispatch<React.SetStateAction<boolean>>
  setRenda: React.Dispatch<React.SetStateAction<boolean>>
  setTelefoneCheckBox: React.Dispatch<React.SetStateAction<CheckboxProps[]>>
  setEmailCheckBox: React.Dispatch<React.SetStateAction<CheckboxProps[]>>
  clientData: DevedorData
  ultimoCPF: string
  telefones: AtualizaTelefoneProps[]
  emails: AtualizaEmailProps[]
  inadimplencia: boolean
  endereco: boolean
  renda: boolean
  telefoneCheckBox: CheckboxProps[]
  emailCheckBox: CheckboxProps[]
}

interface CheckboxProps {
  id: string;
  checked: boolean;
}



const CadastroContext = createContext<CadastroContextProps>({} as CadastroContextProps);

export const CadastroProvider = ({ children }: CadastroContextProviderProps) => {
  const { devedorCadastro } = useDividas()
  const { setModal } = useModal()

  const [clientData, setClientData] = useState<DevedorData>({} as DevedorData);
  const [ultimoCPF, setUltimoCPF] = useState('')
  const [inadimplencia, setInadimpelcia] = useState(false)
  const [endereco, setEndereco] = useState(false)
  const [renda, setRenda] = useState(false)
  const [emailCheckBox, setEmailCheckBox] = useState<CheckboxProps[]>([])
  const [telefoneCheckBox, setTelefoneCheckBox] = useState<CheckboxProps[]>([])

  const [telefones, setTelefones] = useState<AtualizaTelefoneProps[]>([])
  const [emails, setEmails] = useState<AtualizaEmailProps[]>([])
  const mutation = useMutation({
    mutationFn: (dados: AtualizaCadastroProps) => {
      return apiModulo.put('/debtor', {
        cpf_cnpj: dados.cpf_cnpj,
        url: dados.url,
        codcredor: dados.codcredor,
        dadoscadastrais: dados.dadoscadastrais,
        enderecos: dados.enderecos,
        telefones: dados.telefones,
        emails: dados.emails,
        app: dados.app ? dados.app : 'ACIONA_WEB',
        usuario: dados.usuario
      })
    }, mutationKey: ['form'],
    onError: () => setModal('Erro ao atualizar devedor!', 'Erro', false, true)
  })

  useEffect(() => {
    if (Array.isArray(devedorCadastro?.telefones?.telefonecliente)) {
      setTelefones(devedorCadastro?.telefones?.telefonecliente)
    } else {
      setTelefones([])
    }
    if (devedorCadastro?.emails?.emailcliente) {
      setEmails(devedorCadastro?.emails?.emailcliente)
    } else {
      setEmails([])
    }

  }, [devedorCadastro])

  useEffect(() => {
    if (Array.isArray(devedorCadastro?.telefones?.telefonecliente)) {
      telefones.sort((a, b) => {
        const order = {
          "Movel Particular": 1,
          "Movel Comercial": 2,
          "Movel Referencia": 3,
          "Movel Conjuge": 4,
          "Fixo Residencial": 5,
          "Fixo Comercial": 6,
          "Fixo Referencia": 7,
          "Fixo Conjuge": 8,
          "Fixo Trabalho Conjuge": 9,
          "Outros": 10
        };


        // Prioridade por tipo de telefone e situação
        let priorityA = order[a.tipo_telefone] || 11;
        let priorityB = order[b.tipo_telefone] || 11;

        // Verificar se é "contactado" ou "valido" e ajustar a prioridade

        if (a.situacaotelefone.includes('Invalido')) priorityA = 12
        if (b.situacaotelefone.includes('Invalido')) priorityB = 12

        if (a.situacaotelefone !== "Valido" && a.situacaotelefone !== "Contactado") priorityA -= 0.2;
        if (b.situacaotelefone !== "Valido" && b.situacaotelefone !== "Contactado") priorityB -= 0.2;

        if (a.situacaotelefone === "Contactado") priorityA -= 0.8;
        if (b.situacaotelefone === "Contactado") priorityB -= 0.8;

        if (a.situacaotelefone === "Valido") priorityA -= 0.5;
        if (b.situacaotelefone === "Valido") priorityB -= 0.5;



        // Ordenar com base na prioridade e, em caso de empate, pelo tipo de telefone
        if (priorityA === priorityB) {
          return a.tipo_telefone.localeCompare(b.tipo_telefone);
        }

        return priorityA - priorityB;
      });
    }
  }, [telefones])

  function clearCadastro() {
    setEmails([])
    setTelefones([])
  }

  function getClientData(data: React.SetStateAction<DevedorData>) {
    setClientData(data)
  }

  function getUltimoCPF(cpf: string) {
    setUltimoCPF(cpf)
  }

  function getTelefones(telefone: AtualizaTelefoneProps, infoTel?: string) {
    if (!telefone.id_telefone && infoTel && telefones.find(item => item.telefone === infoTel)) {
      const telA = telefones?.find(item => item.telefone === infoTel)
      if (telA && telA?.situacaotelefone !== telefone.situacaotelefone) {
        const index = telefones.indexOf(telA);
        const updatedTel = { ...telA, ...telefone };
        const newTels = [...telefones.slice(0, index), updatedTel, ...telefones.slice(index + 1)];
        setTelefones(newTels)
      }
      return;
    }

    const findByTelInfo = (infoTel: string) => telefones?.find(item => item.telefone === infoTel);
    const findByIdTel = (id_telefone: string) => telefones?.find(item => item.id_telefone === id_telefone);

    if (infoTel) {
      const existingTel = findByTelInfo(infoTel);

      if (existingTel) {
        const index = telefones.indexOf(existingTel);
        const updatedTel = { ...existingTel, ...telefone };
        const newTels = [...telefones.slice(0, index), updatedTel, ...telefones.slice(index + 1)];
        setTelefones(newTels)
        return;
      }
    }

    if (telefone.id_telefone) {
      const existingTel = findByIdTel(telefone.id_telefone);

      if (existingTel) {
        const index = telefones.indexOf(existingTel);
        const updatedTel = { ...existingTel, ...telefone };
        const newTels = [...telefones.slice(0, index), updatedTel, ...telefones.slice(index + 1)];

        setTelefones(newTels)
        return;
      }
    }
    setTelefones([...telefones, telefone])
  }

  function getEmail(email: AtualizaEmailProps, emailInfo?: string) {

    if (!email.id_email && emailInfo && emails.find(item => item.email === emailInfo)) {
      const emailA = emails.find(item => item.email === emailInfo)
      if (emailA && emailA?.situacaoemail !== email.situacaoemail) {
        const index = emails.indexOf(emailA);
        const updatedEmail = { ...emailA, ...email };
        const newEmails = [...emails.slice(0, index), updatedEmail, ...emails.slice(index + 1)];
        setEmails(newEmails)
      }
      return;
    }

    const findByEmailInfo = (emailInfo: string) => emails.find(item => item.email === emailInfo);
    const findByIdEmail = (id_email: string) => emails.find(item => item.id_email === id_email);

    if (emailInfo) {
      const existingEmail = findByEmailInfo(emailInfo);

      if (existingEmail) {
        const index = emails.indexOf(existingEmail);
        const updatedEmail = { ...existingEmail, ...email };
        const newEmails = [...emails.slice(0, index), updatedEmail, ...emails.slice(index + 1)];
        setEmails(newEmails)
        return;
      }
    }

    if (email.id_email) {
      const existingEmail = findByIdEmail(email.id_email);

      if (existingEmail) {
        const index = emails.indexOf(existingEmail);
        const updatedEmail = { ...existingEmail, ...email };
        const newEmails = [...emails.slice(0, index), updatedEmail, ...emails.slice(index + 1)];

        setEmails(newEmails)
        return;
      }
    }
    setEmails([...emails, email])
  }

  function getMelhoresHorarios(melhorhorario: MelhorHorarioProps[], telefone: string) {
    // console.log(telefone, melhorhorario)
    const index = telefones.findIndex(item => item.telefone === telefone)
    if (index !== -1) {
      const novoArray = telefones.map((tel, i) => {
        if (i === index) {

          if (String(tel.melhoreshorarios) === ' ') {
            tel.melhoreshorarios = { melhorhorario: [] };
          }

          melhorhorario.forEach(novoHorario => {
            const horarioExistenteIndex = tel.melhoreshorarios?.melhorhorario?.findIndex(
              existente => existente.dia === novoHorario.dia
            );
            if (horarioExistenteIndex !== -1) {
              const horarioExistente = tel.melhoreshorarios.melhorhorario[horarioExistenteIndex];
              if (
                (novoHorario.horafinal !== horarioExistente.horafinal) ||
                (novoHorario.horainicial !== horarioExistente.horainicial)
              ) {
                tel.melhoreshorarios.melhorhorario[horarioExistenteIndex] = {
                  ...horarioExistente,
                  horafinal: novoHorario.horafinal,
                  horainicial: novoHorario.horainicial
                };
              }
            } else {
              tel.melhoreshorarios.melhorhorario.push(novoHorario);
            }
          });
        }
        return { ...tel };
      });
      setTelefones(novoArray);
    }
  }

  function deleteMelhorHoario(dia: string, telefone: string) {
    setTelefones((prevTelefones) =>
      prevTelefones.map((telefoneItem) => {
        if (telefoneItem.telefone === telefone) {
          const novoTelefoneItem = { ...telefoneItem };
          novoTelefoneItem.melhoreshorarios.melhorhorario = novoTelefoneItem.melhoreshorarios.melhorhorario.filter(
            (horario) => horario.dia !== dia
          );
          return novoTelefoneItem;
        }
        return telefoneItem;
      })
    );
  }

  async function atualizaDevedorCadastro({ codcredor, cpf_cnpj, url, email, telefone, dadosCadastrais, endereco, usuario, nomeclien, datnasc, app }: AtualizaDevedorProps) {

    let dados: AtualizaCadastroProps = {
      cpf_cnpj,
      url,
      codcredor,
      nomeclien,
      datnasc,
      emails: {
        emailcliente: email?.email ? [...emails, email] : emails
      },
      telefones: {
        telefonecliente: telefone?.telefone ? [...telefones, telefone] : telefones
      },
      usuario,
      app
    }

    if (endereco?.tipo_logradouro) {
      dados.enderecos = {
        endereco
      }

    }
    if (dadosCadastrais?.dia_pgto) {
      dados.dadoscadastrais = dadosCadastrais
    }
    const { status } = await mutation.mutateAsync(dados)
    // const status = 200
    if (status === 200) {
      return 200
    }
    return 500
  }

  return (
    <CadastroContext.Provider value={{
      getEmail,
      getTelefones,
      atualizaDevedorCadastro,
      getMelhoresHorarios,
      deleteMelhorHoario,
      emails,
      telefones,
      clearCadastro,
      clientData,
      getClientData,
      getUltimoCPF,
      ultimoCPF,
      endereco,
      inadimplencia,
      renda,
      setEndereco,
      setInadimpelcia,
      setRenda,
      emailCheckBox,
      setEmailCheckBox,
      setTelefoneCheckBox,
      telefoneCheckBox
    }}>
      {children}
    </CadastroContext.Provider>
  );
};

export const useCadastro = () => useContext(CadastroContext);